import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi, ProjectApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {hasReturnedMoneyMetric} from "../../utils/reportingFinancialCompleteness.js";
import {partialProfitabilityChartPoint, partialProfitabilityMarginPercent, selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import {calculatePnoPercent} from "../../utils/reportingMetricRatios.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import {mediaSourceQualityBlocksMetrics, snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import {loadReportingResource, mergeReportingRowMetadata} from "../../utils/reportingIndependentRequest.js";
import {
    ChannelMixColors,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    KpiCard,
    MetricHelpLabel,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

const SuspiciousDomainPatterns = [
    "facebook.com",
    "fb.com",
    "instagram.com",
    "google.com",
    "gopay.com",
    "seznam.cz",
    "localhost",
];
const UNASSIGNED_MEDIA_DOMAIN_ID = "unassigned-media-domain";

function isExplicitZero(value) {
    return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) && Number(value) === 0;
}

function isSuspiciousDomain(hostName) {
    const normalized = String(hostName ?? "").toLowerCase();
    return SuspiciousDomainPatterns.some((pattern) => normalized === pattern || normalized.endsWith(`.${pattern}`));
}

function domainName(domainWithStats) {
    return domainWithStats?.projectDomain?.hostName ?? "-";
}

export function suspiciousDomainRows(rows, selectedDomainIds = []) {
    const selected = Array.isArray(selectedDomainIds) ? selectedDomainIds : [];
    return (rows ?? []).filter((row) => row?.suspicious === true
        && row?.id !== UNASSIGNED_MEDIA_DOMAIN_ID
        && typeof row?.id === "string"
        && row.id.length > 0
        && (selected.length === 0 || selected.includes(row.id)));
}

export function visibleDomainRows(rows, selectedDomainIds = []) {
    const selected = Array.isArray(selectedDomainIds) ? selectedDomainIds : [];
    if (selected.length === 0) return rows ?? [];
    return (rows ?? []).filter((row) => row?.id !== UNASSIGNED_MEDIA_DOMAIN_ID && selected.includes(row?.id));
}

export function hasDomainActivity(rows) {
    return (rows ?? []).some((row) => Number(row?.orders) > 0
        || moneyAmount(row?.marketing) !== 0
        || Number(row?.campaignCount) > 0
        || Number(row?.adCount) > 0);
}

export function shouldRenderProfitMetric(hasReturnedProfitField) {
    return hasReturnedProfitField;
}

export function domainCentreAttributionStatus(attribution, selectedCentreFilter) {
    return selectedCentreFilter
        ? selectedCentreSpendAttributionStatus(attribution, true)
        : null;
}

export function domainOverallProfitMetric(partialProfitability, snapshotIncomplete = false, attributionStatus = null, mediaSourceQuality = null) {
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(mediaSourceQuality);
    const usableScope = !snapshotIncomplete && attributionStatus === null && !mediaQualityBlocked;
    const standardSelection = selectProfitabilityMetric({
        fullValue: usableScope ? partialProfitability?.knownProfitAfterAllMarketing : undefined,
        fullComplete: usableScope && partialProfitability?.complete === true,
        partialProfitability: usableScope ? partialProfitability : null,
        partialField: "knownProfitAfterAllMarketing",
    });
    return {
        ...selectReportProfitabilityMetric({standardSelection, partialProfitability: mediaQualityBlocked ? null : partialProfitability, assignedField: "profitAfterAssignedMarketing", snapshotIncomplete}),
        partialProfitability,
    };
}

export function domainCostCoverageCompleteness(partialProfitability) {
    return typeof partialProfitability?.complete === "boolean" ? partialProfitability.complete : undefined;
}

function selectedCompleteDomainRow(rows, selectedDomainIds, snapshotIncomplete, attributionStatus) {
    if (snapshotIncomplete || attributionStatus !== null || !Array.isArray(selectedDomainIds) || selectedDomainIds.length !== 1 || !Array.isArray(rows) || rows.length !== 1) return null;

    const selectedDomainId = selectedDomainIds[0];
    const row = rows[0];
    const performance = row?.performance;
    const partial = performance?.partialProfitability;
    const profit = performance?.orderProfitNet;
    const profitAmount = Number(profit?.amount);

    if (row?.id !== selectedDomainId || row?.domain?.projectDomain?.id !== selectedDomainId
        || performance?.purchaseCostQualityAvailable !== true
        || performance?.purchaseCostCompleteNet !== true
        || !isExplicitZero(performance?.purchaseCostUnknownNet)
        || partial?.complete !== true
        || profit?.amount === null || profit?.amount === undefined || profit?.amount === ""
        || !Number.isFinite(profitAmount)) return null;

    return {row, performance, partial};
}

export function domainSingleSelectionProfitMetric(rows, selectedDomainIds, snapshotIncomplete = false, attributionStatus = null) {
    const selected = selectedCompleteDomainRow(rows, selectedDomainIds, snapshotIncomplete, attributionStatus);
    if (!selected) return null;

    const metrics = domainPerformanceMetrics(selected.performance, attributionStatus);
    if (metrics.profit === undefined) return null;

    return {
        value: metrics.profit,
        allowed: true,
        partial: metrics.profitPartial,
        estimated: metrics.profitEstimated,
        assignedMarketing: metrics.profitUsesAssignedMarketing,
        partialProfitability: metrics.partialProfitability,
    };
}

export function domainCostCoverageForSingleSelection(overallPartialProfitability, rows, selectedDomainIds, snapshotIncomplete = false, attributionStatus = null) {
    const selected = selectedCompleteDomainRow(rows, selectedDomainIds, snapshotIncomplete, attributionStatus);
    const isVerifiedEmptyCohort = selected?.partial?.available === false
        && isExplicitZero(selected.partial.knownLineCount)
        && isExplicitZero(selected.partial.unknownLineCount)
        && isExplicitZero(selected.performance.orderAmountNet)
        && isExplicitZero(selected.performance.orderTurnoverNet?.amount)
        && isExplicitZero(selected.performance.orderSpendGoodsNet?.amount)
        && isExplicitZero(selected.performance.purchaseCostUnknownNet);

    return isVerifiedEmptyCohort
        ? {data: undefined, costComplete: true, unknownCount: 0, estimatedCount: 0}
        : {
            data: overallPartialProfitability,
            costComplete: domainCostCoverageCompleteness(overallPartialProfitability),
            unknownCount: overallPartialProfitability?.unknownLineCount,
            estimatedCount: overallPartialProfitability?.estimatedLineCount,
        };
}

export function domainPerformanceMetrics(performance, attributionStatus = null) {
    const turnover = performance?.orderTurnoverNet;
    const partialProfitability = performance?.partialProfitability;
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(performance?.mediaSourceQuality);
    const standardSelection = selectProfitabilityMetric({
        fullValue: mediaQualityBlocked ? undefined : performance?.orderProfitNet,
        fullComplete: !mediaQualityBlocked && (partialProfitability === undefined ? performance?.orderProfitNet !== undefined : partialProfitability.complete === true),
        partialProfitability: !mediaQualityBlocked && attributionStatus === null ? partialProfitability : null,
        partialField: "knownProfitAfterAllMarketing",
    });
    const profitSelection = selectReportProfitabilityMetric({standardSelection, partialProfitability: mediaQualityBlocked ? null : partialProfitability, assignedField: "profitAfterAssignedMarketing"});
    const profit = profitSelection.value;
    const marketing = performance?.orderSpendMarketing;
    const adMetricsAvailable = attributionStatus === null && !mediaQualityBlocked;
    const pno = mediaQualityBlocked ? null : calculatePnoPercent(marketing?.amount, turnover?.amount);
    const margin = profit != null
        ? profitSelection.assignedMarketing
            ? null
            : profitSelection.partial
            ? partialProfitabilityMarginPercent(partialProfitability)
            : dividePercent(moneyAmount(profit), moneyAmount(turnover))
        : null;

    return {
        profit,
        pno,
        margin,
        chartPno: adMetricsAvailable ? pno : null,
        chartMargin: adMetricsAvailable ? margin : null,
        profitIsSubtotal: !adMetricsAvailable && profit != null,
        profitPartial: profitSelection.partial,
        profitUsesAssignedMarketing: profitSelection.assignedMarketing,
        profitEstimated: !profitSelection.partial && profit != null && partialProfitability?.quality === "estimated" && Number(partialProfitability?.estimatedLineCount) > 0,
        partialProfitability,
        profitAllowed: profitSelection.allowed,
    };
}

export default function ReportingDomainsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [domains, setDomains] = useState([]);
    const [performanceByDomain, setPerformanceByDomain] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [centreSpendAttribution, setCentreSpendAttribution] = useState(null);
    const [overallPartialProfitability, setOverallPartialProfitability] = useState(undefined);
    const [overallMediaSourceQuality, setOverallMediaSourceQuality] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metadataLoading, setMetadataLoading] = useState(true);
    const [metadataError, setMetadataError] = useState(false);
    const [reportError, setReportError] = useState(false);
    const [metadataRetry, setMetadataRetry] = useState(0);
    const [reportRetry, setReportRetry] = useState(0);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const eligibleDomains = useMemo(() => domains
        .filter((domain) => domain?.projectDomain?.isEnabled)
        .filter((domain) => (domainsAllowListed ?? []).length === 0 || domainsAllowListed.includes(domain?.projectDomain?.id))
        .sort((a, b) => domainName(a).localeCompare(domainName(b), "cs")), [domains, domainFilterKey]);
    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setPerformanceByDomain([]);
        setSnapshot(null);
        setCentreSpendAttribution(null);
        setOverallPartialProfitability(undefined);
        setOverallMediaSourceQuality(null);

        const authorityConfiguration = getAuthorityConfiguration();
        const erpApi = new ErpCalculationApi(authorityConfiguration);

        setReportError(false);
        loadReportingResource({
            request: () => erpApi.shopPerformanceByDomain({
                projectId: project.id,
                erpFilter: {
                    since: sinceString,
                    until: untilString,
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            }),
            isActive: () => active,
            onSuccess: (dimensionData) => {
                if (!active) {
                    return;
                }

                setSnapshot(dimensionData?.snapshot ?? null);
                setCentreSpendAttribution(dimensionData?.centreSpendAttribution ?? null);
                setOverallPartialProfitability(dimensionData?.partialProfitability);
                setOverallMediaSourceQuality(dimensionData?.mediaSourceQuality ?? null);
                setPerformanceByDomain((dimensionData?.rows ?? []).map((row) => ({
                    domain: undefined,
                    id: row.id,
                    name: row.name,
                    performance: row.performance,
                    partialProfitability: row.partialProfitability,
                })));
            },
            onError: (error) => {
                if (!active) {
                    return;
                }

                setReportError(true);
                et(error).then((message) => { if (message) toast.error(message); });
            },
            onFinally: () => { if (active) setLoading(false); },
        });

        return () => {
            active = false;
        };
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey, reportRetry]);

    useEffect(() => {
        if (!project?.id) return undefined;

        let active = true;
        setMetadataLoading(true);
        setMetadataError(false);
        setDomains([]);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        loadReportingResource({
            request: () => projectApi.listProjectDomains({projectId: project.id}),
            isActive: () => active,
            onSuccess: (nextDomains) => setDomains(nextDomains ?? []),
            onError: (error) => {
                setMetadataError(true);
                et(error).then((message) => { if (message) toast.error(message); });
            },
            onFinally: () => setMetadataLoading(false),
        });

        return () => { active = false; };
    }, [project?.id, metadataRetry]);

    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const selectedCentreFilter = (centresAllowListed ?? []).length > 0;
    const attributionStatus = loading ? null : domainCentreAttributionStatus(centreSpendAttribution, selectedCentreFilter);
    const rows = useMemo(() => visibleDomainRows(snapshotRowsForPublishedDays(mergeReportingRowMetadata(
        performanceByDomain,
        domains,
        {getRowId: (row) => row.id, getMetadataId: (domain) => domain?.projectDomain?.id, property: "domain"},
    ), snapshot), domainsAllowListed)
        .map(({domain, name, id, performance, partialProfitability}) => {
            const turnover = performance?.orderTurnoverNet;
            const metricPerformance = performance?.partialProfitability === undefined && partialProfitability !== undefined
                ? {...performance, partialProfitability}
                : performance;
            const {profit, pno, margin, chartPno, chartMargin, profitPartial, profitEstimated, profitUsesAssignedMarketing} = domainPerformanceMetrics(metricPerformance, attributionStatus);
            const hasProfit = profit != null;
            const marketing = performance?.orderSpendMarketing;
            const orders = Number(performance?.orderAmountNet ?? 0);

            return {
                id: domain?.projectDomain?.id ?? id,
                performance: metricPerformance,
                name: id === UNASSIGNED_MEDIA_DOMAIN_ID
                    ? t("reporting_lab.domains.unassigned_media_domain", {defaultValue: "Nepřiřazená doména"})
                    : domainName(domain) === "-" ? name : domainName(domain),
                domain,
                turnover,
                profit,
                profitPartial,
                profitEstimated,
                profitUsesAssignedMarketing,
                partialProfitability: metricPerformance?.partialProfitability,
                marketing,
                orders,
                ...(hasProfit ? {margin} : {}),
                pno,
                chartPno,
                chartMargin,
                campaignCount: Number(domain?.projectDomainStats?.campaignCount ?? 0),
                adCount: Number(domain?.projectDomainStats?.adCount ?? 0),
                suspicious: isSuspiciousDomain(domainName(domain)),
            };
        })
        .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover)), [performanceByDomain, domains, snapshot, attributionStatus, domainFilterKey, domainsAllowListed, t]);
    const suspiciousDomains = useMemo(() => suspiciousDomainRows(rows, domainsAllowListed), [rows, domainFilterKey]);
    const activeSuspiciousDomains = suspiciousDomains.filter((row) => row.domain?.projectDomain?.isEnabled);

    const currency = rows.find((row) => row.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = snapshotPeriodValue(rows.reduce((sum, row) => sum + moneyAmount(row.turnover), 0), snapshot);
    const hasProfit = hasReturnedMoneyMetric(rows, "profit");
    const totalProfitSelection = domainSingleSelectionProfitMetric(rows, domainsAllowListed, summaryUnavailable, attributionStatus)
        ?? domainOverallProfitMetric(overallPartialProfitability, summaryUnavailable, attributionStatus, overallMediaSourceQuality);
    const totalProfitContext = totalProfitSelection.partialProfitability ?? overallPartialProfitability;
    const totalProfitAssignedResult = totalProfitSelection.assignedMarketing ? totalProfitContext?.assignedMarketingResult : null;
    const totalProfit = totalProfitSelection.value == null ? null : moneyAmount(totalProfitSelection.value);
    const hasCompleteProfit = totalProfit !== null && !totalProfitSelection.partial;
    const hasPartialProfit = totalProfit !== null && totalProfitSelection.partial;
    const showProfitMetric = shouldRenderProfitMetric(totalProfitSelection.allowed);
    const costCoverage = domainCostCoverageForSingleSelection(overallPartialProfitability, rows, domainsAllowListed, summaryUnavailable, attributionStatus);
    const tableProfitContext = totalProfitSelection.partialProfitability ?? costCoverage.data ?? overallPartialProfitability;
    const tableProfitAssignedResult = totalProfitSelection.assignedMarketing ? tableProfitContext?.assignedMarketingResult : null;
    const hasVisibleDomainActivity = hasDomainActivity(rows);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const turnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...rows].slice(0, 10).reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 180, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: [...rows].slice(0, 10).reverse().map((row, index) => ({
                    value: moneyToMajor(row.turnover),
                    itemStyle: {color: ChannelMixColors[(rows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"})),
    };

    const shareOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: rows.slice(0, 8).map((row) => ({
                    name: row.name,
                    value: moneyToMajor(row.turnover),
                })),
            },
        ],
    };

    const efficiencyOption = {
        color: ["#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: rows.slice(0, 10).map((row) => row.name),
            axisLabel: {fontSize: 12, width: 90, overflow: "truncate"},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"}),
                type: "bar",
                data: rows.slice(0, 10).map((row) => partialProfitabilityChartPoint(row.chartMargin, row.partialProfitability, row.profitPartial, t)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
                type: "bar",
                data: rows.slice(0, 10).map((row) => row.chartPno ?? null),
                barMaxWidth: 28,
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"})),
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.domains", {defaultValue: "Domény"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[activeSuspiciousDomains.length > 0 ? {
            id: "suspicious-domains",
            type: "info",
            showClose: false,
            title: t("reporting_lab.domains.audit_warning_title", {defaultValue: "Technické a službové domény"}),
            content: t("reporting_lab.domains.audit_warning", {defaultValue: "Tyto domény vypadají jako reklamní, platební nebo technické domény. Pokud jsou zapnuté jako reportované domény projektu, mohou zkreslovat filtry, statistiky i billing."}),
        } : null]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.domains.title", {defaultValue: "K čemu slouží doménový přehled"})}
                >
                    {t("reporting_lab.descriptions.domains.text", {defaultValue: "Tržby, objednávky a zisk po reklamě rozděluje podle e-shopových domén. Ekonomické metriky vycházejí z dokončených objednávek."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                {metadataError && <div role="alert" className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    <span>{t("reporting_lab.domains.metadata_unavailable", {defaultValue: "Seznam domén se nepodařilo načíst. Report může zobrazit dostupná data, ale názvy a počty domén nemusí být úplné."})}</span>
                    <button type="button" className="rounded-md border border-amber-300 px-3 py-1.5 font-medium hover:bg-amber-100 dark:border-amber-800 dark:hover:bg-amber-900/40" onClick={() => setMetadataRetry((value) => value + 1)}>
                        {t("reporting_lab.marketing.retry", {defaultValue: "Zkusit znovu"})}
                    </button>
                </div>}
                {reportError && <div role="alert" className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
                    <span>{t("reporting_lab.domains.report_unavailable", {defaultValue: "Report domén se nepodařilo načíst. Hodnoty nejsou dostupné."})}</span>
                    <button type="button" className="rounded-md border border-rose-300 px-3 py-1.5 font-medium hover:bg-rose-100 dark:border-rose-800 dark:hover:bg-rose-900/40" onClick={() => setReportRetry((value) => value + 1)}>
                        {t("reporting_lab.marketing.retry", {defaultValue: "Zkusit znovu"})}
                    </button>
                </div>}
                {!reportError && <>
                <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={overallMediaSourceQuality}/>
                <ReportingCostCoverageContext data={costCoverage.data} reportContext="domains" loading={loading}/>
                </>}

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading || metadataLoading} label={t("reporting_lab.domains.active_domains", {defaultValue: "Aktivní domény"})} value={loading || metadataLoading || metadataError || reportError ? "—" : renderNumber(eligibleDomains.length)} meta={t("reporting_lab.domains.filtered_domains", {defaultValue: "Dle aktuálního filtru"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} value={loading || totalTurnover == null ? "—" : renderCurrencyValue({amount: totalTurnover, currency}, true)}/>
                </div>
                {showProfitMetric && <div className="col-span-12 md:col-span-3">
                    <KpiCard
                        loading={loading}
                        label={totalProfitSelection.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.kpi.profit", {defaultValue: "Hrubý zisk po reklamě"})}
                        value={loading || totalProfit === null ? "—" : renderCurrencyValue(totalProfitSelection.value, true)}
                        meta={hasCompleteProfit || hasPartialProfit ? null : summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : attributionStatus === "unknown" ? t("reporting_lab.centre_spend_attribution.selected_unknown_short", {defaultValue: "Úplnost souhrnu vybraných středisek nelze ověřit"}) : attributionStatus === "incomplete" ? t("reporting_lab.centre_spend_attribution.selected_short", {defaultValue: "Souhrn vybraných středisek je neúplný"}) : t("reporting_lab.cost_quality.incomplete_short", {defaultValue: "Neúplné náklady"})}
                        metaTone={!hasCompleteProfit && !hasPartialProfit ? "unavailable" : "default"}
                        info={<><ProfitabilityMetricTooltip metric="profit" partialProfitability={totalProfitContext} assignedResult={totalProfitAssignedResult} assignedScope={rows.some((row) => row.profitUsesAssignedMarketing) && !totalProfitSelection.assignedMarketing} attributionStatus={attributionStatus} rowScopes={rows.map((row) => row.partialProfitability)} t={t}/>{!hasCompleteProfit && !hasPartialProfit && !summaryUnavailable && <div className="mt-1">{attributionStatus === "unknown" ? t("reporting_lab.centre_spend_attribution.unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Souhrn vybraných středisek proto nelze ověřit."}) : attributionStatus === "incomplete" ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."}) : t("reporting_lab.cost_quality.incomplete", {defaultValue: "Část nákladových podkladů chybí nebo není ověřena. Hrubý zisk po reklamě proto nelze za celé období spolehlivě vyčíslit."})}</div>}</>}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                        tone={totalProfit !== null && totalProfit < 0 ? "bad" : "default"}
                    />
                </div>}
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.domains.suspicious_domains", {defaultValue: "Technické a službové domény"})} value={loading ? "—" : renderNumber(suspiciousDomains.length)} meta={loading ? "—" : suspiciousDomains[0]?.name ?? t("reporting_lab.domains.none", {defaultValue: "Žádné"})} emphasizeMeta={false}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.domains.revenue_by_domain", {defaultValue: "Obrat podle domény"})} className="col-span-12 xl:col-span-7">
                    <EChart option={turnoverOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.domains.revenue_share", {defaultValue: "Rozpad top 8 domén"})} className="col-span-12 xl:col-span-5">
                    <EChart option={shareOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.domains.efficiency", {defaultValue: "Efektivita domén"})} className="col-span-12">
                    <EChart option={efficiencyOption} className="h-80"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.domains.table", {defaultValue: "Přehled domén"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.domains.domain", {defaultValue: "Doména"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                {hasProfit && <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})} tooltip={<ProfitabilityMetricTooltip metric="profit" partialProfitability={tableProfitContext} assignedResult={tableProfitAssignedResult} assignedScope={rows.some((row) => row.profitUsesAssignedMarketing) && !totalProfitSelection.assignedMarketing} attributionStatus={attributionStatus} rowScopes={rows.map((row) => row.partialProfitability)} t={t}/>} /></th>}
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.pno", {defaultValue: "PNO"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.domains.campaigns", {defaultValue: "Kampaně"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.domains.ads", {defaultValue: "Reklamy"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {rows.map((row, index) => <tr key={row.id}>
                                <td className="py-3 pr-4">
                                    <div className="font-semibold text-slate-900 dark:text-slate-100">{row.name}</div>
                                    {row.suspicious && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t("reporting_lab.domains.suspicious", {defaultValue: "Externí služba"})}</div>}
                                </td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                {hasProfit && <td className={`py-3 pr-4 text-right ${row.profit != null && moneyAmount(row.profit) < 0 ? "text-datixo-negative" : "text-slate-600 dark:text-slate-300"}`}><span className="inline-flex items-center justify-end gap-1">{row.profit == null ? "—" : renderCurrencyValue(row.profit, true)}</span></td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.pno)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.orders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.campaignCount)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.adCount)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {!loading && !reportError && !hasVisibleDomainActivity && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.domains.empty", {defaultValue: "Pro vybrané období nejsou dostupná doménová data."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
